/**
 * Utility functions for match-related calculations
 */

interface Participant {
  id?: string;
  profile_id?: string;
  team?: number | null;
  profile?: {
    id?: string;
  };
}

export type MatchTypeForSlots = '1v1' | 'doubles' | 'team' | string;

/**
 * Get the canonical profile id of a match participant row, regardless of
 * whether it was queried as a flat row (`profile_id`) or with an embed
 * (`profile.id`). Never returns the `match_participants.id` (row id).
 */
export function getParticipantProfileId(p: Participant | null | undefined): string | null {
  if (!p) return null;
  return p.profile_id ?? p.profile?.id ?? null;
}

/**
 * Count participants other than the match creator.
 * Safe across 1v1 (creator usually not in match_participants), doubles
 * and team matches (creator is inserted as a participant).
 */
function countNonCreatorParticipants(
  participants: Participant[] | undefined | null,
  creatorId: string | null | undefined,
): number {
  if (!participants || participants.length === 0) return 0;
  if (!creatorId) return participants.length;
  return participants.filter((p) => getParticipantProfileId(p) !== creatorId).length;
}

/**
 * For team matches, "recruiting_slots" represents the number of OPPOSING TEAMS
 * still needed (typically 1 — one opposing team). It must NOT be decremented by
 * teammates of the creator. We count it as filled once any participant from a
 * team other than the creator's team is present.
 */
function countOpposingTeamsPresent(
  participants: Participant[] | undefined | null,
  creatorTeam: number,
): number {
  if (!participants || participants.length === 0) return 0;
  const opposingTeams = new Set<number>();
  for (const p of participants) {
    if (typeof p.team === 'number' && p.team !== creatorTeam) {
      opposingTeams.add(p.team);
    }
  }
  return opposingTeams.size;
}

/**
 * Calculate remaining recruiting slots.
 * - 1v1 / doubles: `recruiting_slots` = seats for individual recruits (excludes creator).
 * - team + slots === 1: `recruiting_slots` = number of opposing TEAMS still needed.
 * - team + slots > 1: `recruiting_slots` = individuals on the non-creator side.
 */
export function calculateRemainingSlots(
  recruitingSlots: number,
  participants: Participant[] | undefined | null,
  creatorId: string | null | undefined,
  matchType?: MatchTypeForSlots,
  creatorTeam: number = 1,
): number {
  if (!recruitingSlots || recruitingSlots <= 0) return 0;
  if (matchType === 'team' && recruitingSlots === 1) {
    const opposing = countOpposingTeamsPresent(participants, creatorTeam);
    return Math.max(0, recruitingSlots - opposing);
  }
  if (matchType === 'team') {
    const opposingIndividuals = (participants ?? []).filter(
      (p) => typeof p.team === 'number' && p.team !== creatorTeam,
    ).length;
    return Math.max(0, recruitingSlots - opposingIndividuals);
  }
  const recruited = countNonCreatorParticipants(participants, creatorId);
  return Math.max(0, recruitingSlots - recruited);
}

/**
 * Recruitment unit label for a match. In 1v1 (and equivalents) the host is
 * looking for individual players; in team matches the host is looking for
 * whole opposing teams.
 */
export function getRecruitUnit(
  matchType: MatchTypeForSlots | null | undefined,
): 'người' | 'đội' {
  return matchType === 'team' ? 'đội' : 'người';
}

/**
 * Total people currently in the match (creator + non-creator participants).
 * Normalised so 1v1 / doubles / team all return a consistent count even if
 * the creator is or isn't present in the participants array.
 */
export function countMatchParticipants(
  participants: Participant[] | undefined | null,
  creatorId: string | null | undefined,
): number {
  const nonCreator = countNonCreatorParticipants(participants, creatorId);
  return creatorId ? nonCreator + 1 : nonCreator;
}

const MIN_MATCH_DURATION_MINUTES = 10;

/**
 * Check if a ranked match has sufficient duration for ranking points
 */
export function checkMatchDuration(startedAt: string | null | undefined): {
  isValid: boolean;
  durationMinutes: number;
  minRequired: number;
} {
  if (!startedAt) {
    return { isValid: true, durationMinutes: 0, minRequired: MIN_MATCH_DURATION_MINUTES };
  }
  
  const durationMs = Date.now() - new Date(startedAt).getTime();
  const durationMinutes = Math.floor(durationMs / 60000);
  
  return {
    isValid: durationMinutes >= MIN_MATCH_DURATION_MINUTES,
    durationMinutes,
    minRequired: MIN_MATCH_DURATION_MINUTES,
  };
}

/**
 * Compute a robust timestamp for sorting match-like records.
 * Priority: completed_at -> updated_at -> created_at -> date+time fallback.
 * Returns 0 when nothing parses.
 */
export function getMatchTimestamp(m: any): number {
  if (!m) return 0;
  const candidates = [m.completed_at, m.updated_at, m.created_at];
  for (const c of candidates) {
    if (c) {
      const t = new Date(c).getTime();
      if (!isNaN(t)) return t;
    }
  }
  if (m.date) {
    const datePart = String(m.date).slice(0, 10);
    const timeRaw = m.time ? String(m.time).slice(0, 8) : '00:00:00';
    const timePart = timeRaw.length === 5 ? `${timeRaw}:00` : timeRaw;
    const t = new Date(`${datePart}T${timePart}`).getTime();
    if (!isNaN(t)) return t;
  }
  return 0;
}

/**
 * Sort matches by recency (newest first) with stable id tiebreaker.
 * Returns a NEW array — does not mutate input.
 */
export function sortMatchesByRecency<T extends { id?: string | number }>(matches: T[]): T[] {
  return [...matches].sort((a, b) => {
    const diff = getMatchTimestamp(b) - getMatchTimestamp(a);
    if (diff !== 0) return diff;
    return String((b as any).id ?? '').localeCompare(String((a as any).id ?? ''));
  });
}

/**
 * Build a robust ISO-like start datetime string for an activity, given
 * a date (YYYY-MM-DD) and an optional time (HH:mm or HH:mm:ss).
 * Returns null if the date is missing or the result is unparsable.
 */
export function buildActivityStartTime(
  date: string | null | undefined,
  time: string | null | undefined,
): string | null {
  if (!date) return null;
  const datePart = String(date).slice(0, 10);
  const timeRaw = time ? String(time).slice(0, 8) : '00:00:00';
  const timePart = timeRaw.length === 5 ? `${timeRaw}:00` : timeRaw;
  const iso = `${datePart}T${timePart}`;
  if (isNaN(new Date(iso).getTime())) return null;
  return iso;
}

/**
 * Build the activity end ISO string from start + duration in minutes.
 * Returns null if start cannot be built.
 */
export function buildActivityEndTime(
  date: string | null | undefined,
  time: string | null | undefined,
  durationMinutes: number | null | undefined,
): string | null {
  const start = buildActivityStartTime(date, time);
  if (!start) return null;
  const startMs = new Date(start).getTime();
  if (isNaN(startMs)) return null;
  const dur = typeof durationMinutes === 'number' && durationMinutes > 0
    ? durationMinutes
    : 180; // default 3h
  return new Date(startMs + dur * 60 * 1000).toISOString();
}


/**
 * Format start–end time range as "HH:mm – HH:mm" given start "HH:mm[:ss]" and duration minutes.
 * Falls back to just "HH:mm" if duration not provided/invalid.
 */
export function formatMatchTimeRange(
  time: string | null | undefined,
  durationMinutes: number | null | undefined,
): string {
  if (!time) return '';
  const start = time.slice(0, 5);
  const dur = typeof durationMinutes === 'number' && durationMinutes > 0 ? durationMinutes : 0;
  if (!dur) return start;
  const [h, m] = start.split(':').map(Number);
  const total = Math.min(h * 60 + m + dur, 23 * 60 + 59);
  const eh = Math.floor(total / 60).toString().padStart(2, '0');
  const em = (total % 60).toString().padStart(2, '0');
  return `${start} – ${eh}:${em}`;
}

export type MatchLifecycleStatus = 'upcoming' | 'ongoing' | 'pending_confirmation' | 'completed' | 'cancelled';

export interface MatchStatusBadge {
  status: MatchLifecycleStatus;
  label: string;
  className: string;
}

/**
 * Compute a human-friendly lifecycle status for a match, based on its `status`,
 * scheduled time, and confirmed result. Used for badges on listing cards.
 */
export function getMatchStatusBadge(match: {
  status?: string | null;
  date?: string | null;
  time?: string | null;
  duration_minutes?: number | null;
  match_results?: Array<{ is_confirmed?: boolean | null }> | null;
}): MatchStatusBadge {
  const raw = (match.status || 'upcoming') as string;
  if (raw === 'cancelled') {
    return {
      status: 'cancelled',
      label: 'Đã hủy',
      className: 'bg-muted text-muted-foreground ring-1 ring-border',
    };
  }

  const result = match.match_results?.[0];
  if (raw === 'completed') {
    if (result?.is_confirmed) {
      return {
        status: 'completed',
        label: 'Đã xong',
        className: 'bg-green-500/15 text-green-600 dark:text-green-400 ring-1 ring-green-500/30',
      };
    }
    return {
      status: 'pending_confirmation',
      label: 'Chờ xác nhận',
      className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/30',
    };
  }

  // Compute live window from scheduled time
  const start = buildActivityStartTime(match.date, match.time);
  const end = buildActivityEndTime(match.date, match.time, match.duration_minutes);
  const now = Date.now();
  if (start && end) {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (raw === 'ongoing' || (now >= startMs && now <= endMs)) {
      return {
        status: 'ongoing',
        label: 'Đang diễn ra',
        className: 'bg-red-500/15 text-red-600 dark:text-red-400 ring-1 ring-red-500/30 animate-pulse',
      };
    }
    if (now > endMs) {
      return {
        status: 'pending_confirmation',
        label: 'Chờ xác nhận',
        className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/30',
      };
    }
  }

  return {
    status: 'upcoming',
    label: 'Sắp diễn ra',
    className: 'bg-primary/15 text-primary ring-1 ring-primary/30',
  };
}
